"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTableDataSource = exports.useAnalyticTableColumnConfig = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const types_1 = require("@src/types");
const dayjs_1 = __importDefault(require("dayjs"));
const react_1 = __importDefault(require("react"));
const react_intl_1 = require("react-intl");
const elements_1 = require("@src/components/elements");
const useConfig_1 = require("@src/hooks/useConfig");
const ReportTable_style_1 = require("./ReportTable.style");
const useAnalyticTableColumnConfig = ({ intl, analyticsType, setTableAnalyticsType, reportStack, setReportStack, }) => {
    const config = (0, useConfig_1.useConfig)();
    const { analyticsDateFormat } = config;
    const columnConfig = react_1.default.useMemo(() => ({
        [types_1.AnalyticsType.applications_per_offer]: {
            columns: [
                {
                    dataIndex: types_1.AnalyticsTableKeys.offerTitle,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_offer_title',
                    }),
                    onCell: ({ title, id: offerId, companyId: offerCompanyId }) => ({
                        onClick: () => {
                            setTableAnalyticsType(types_1.AnalyticsType.offer_applications);
                            setReportStack([
                                ...reportStack,
                                {
                                    orderNumber: reportStack.length + 1,
                                    type: types_1.AnalyticsType.offer_applications,
                                    titleWithValues: {
                                        intlStringKey: 'admin.analytics.offer_applications.offer_title',
                                        values: { offerTitle: title },
                                    },
                                    parameters: { offerId, offerCompanyId: offerCompanyId },
                                },
                            ]);
                        },
                    }),
                    className: 'clickable-cell',
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.offerApplicationsCount,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_offer_application_count',
                    }),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.offerFirstPublishedAt,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_offer_first_published_at',
                    }),
                    render: text => (0, dayjs_1.default)(text).format(analyticsDateFormat),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.offerDeadline,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_offer_deadline',
                    }),
                    render: text => (0, dayjs_1.default)(text).format(analyticsDateFormat),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.name,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_offer_posted_by',
                    }),
                },
            ],
        },
        [types_1.AnalyticsType.offer_applications]: {
            columns: [
                {
                    dataIndex: types_1.AnalyticsTableKeys.name,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_name',
                    }),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.email,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_email',
                    }),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.telephone,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_telephone',
                    }),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.stage,
                    title: intl.formatMessage({ id: 'admin.analytics.report.column_stage' }),
                    render: text => intl.formatMessage({ id: `offer_application.stage.${text}` }),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.offerApplicationCreatedAt,
                    title: intl.formatMessage({ id: 'admin.analytics.report.column_offer_application_created_at' }),
                    render: text => (0, dayjs_1.default)(text).format(analyticsDateFormat),
                },
            ],
        },
        [types_1.AnalyticsType.applications_per_day]: {
            columns: [
                {
                    dataIndex: types_1.AnalyticsTableKeys.day,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_day',
                    }),
                    render: text => (0, dayjs_1.default)(text).format(analyticsDateFormat),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.offerApplicationsCount,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_offer_application_count',
                    }),
                },
            ],
            onRow: (record) => ({
                onClick: () => {
                    const specificDate = (0, dayjs_1.default)(record.day).format(analyticsDateFormat);
                    setTableAnalyticsType(types_1.AnalyticsType.offer_applications);
                    setReportStack([
                        ...reportStack,
                        {
                            orderNumber: reportStack.length + 1,
                            type: types_1.AnalyticsType.offer_applications,
                            titleWithValues: {
                                intlStringKey: 'admin.analytics.day_offer_applications.offer_title',
                                values: { specificDate },
                            },
                            parameters: { specificDate },
                        },
                    ]);
                },
            }),
            summary: (applicationsPerDay) => {
                const applicationsPerDaySum = applicationsPerDay.reduce((partialSum, a) => partialSum + a.offerApplicationsCount, 0);
                return ((0, jsx_runtime_1.jsxs)(ReportTable_style_1.TableSummaryRow, { children: [(0, jsx_runtime_1.jsx)(elements_1.Table.Summary.Cell, { index: 0, children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.analytics.report.summary_total" }) }), (0, jsx_runtime_1.jsx)(elements_1.Table.Summary.Cell, { index: 1, children: (0, jsx_runtime_1.jsx)("div", { children: applicationsPerDaySum }) })] }));
            },
        },
        [types_1.AnalyticsType.day_offer_applications]: {
            columns: [
                {
                    dataIndex: types_1.AnalyticsTableKeys.day,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_day',
                    }),
                    render: text => (0, dayjs_1.default)(text).format(analyticsDateFormat),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.subscribersCount,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_subscribers',
                    }),
                },
            ],
        },
        [types_1.AnalyticsType.accumulated_company_subscribers]: {
            columns: [
                {
                    dataIndex: types_1.AnalyticsTableKeys.day,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_day',
                    }),
                    render: text => (0, dayjs_1.default)(text).format(analyticsDateFormat),
                },
                {
                    dataIndex: types_1.AnalyticsTableKeys.subscribersCount,
                    title: intl.formatMessage({
                        id: 'admin.analytics.report.column_subscribers',
                    }),
                },
            ],
            summary: (accumulatedCompanySubscribers) => {
                const [dayWithSubCount] = accumulatedCompanySubscribers.slice(-1);
                return ((0, jsx_runtime_1.jsxs)(ReportTable_style_1.TableSummaryRow, { children: [(0, jsx_runtime_1.jsx)(elements_1.Table.Summary.Cell, { index: 0, children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "admin.analytics.report.summary_total" }) }), (0, jsx_runtime_1.jsx)(elements_1.Table.Summary.Cell, { index: 1, children: dayWithSubCount && (0, jsx_runtime_1.jsx)("div", { children: dayWithSubCount.subscribersCount }) })] }));
            },
        },
    }), [intl]);
    return columnConfig[analyticsType];
};
exports.useAnalyticTableColumnConfig = useAnalyticTableColumnConfig;
const getTableDataSource = ({ tableData }) => {
    const dataSource = tableData === null || tableData === void 0 ? void 0 : tableData.map((item, index) => {
        const obj = { key: index, ...item };
        return obj;
    });
    return dataSource;
};
exports.getTableDataSource = getTableDataSource;
